import { Avatar, Group, Text } from '@mantine/core';
import { IconRefresh, IconStarFilled } from '@tabler/icons-react';
import { formatRelativeDate } from 'src/utils/dateFormatting';
import { joinNames } from '../../names';
import { modelDetailPath, ScenarioScope } from '../scope';
import { ScenarioSummary } from '../types';
import classes from './ModelList.module.css';
import { SyncStatus } from './SyncStatus';

// A family model's canonical page lives on the family route, wherever it is listed;
// individual models stay on the client route.
export const modelTarget = (scenario: ScenarioSummary, scope: ScenarioScope): string =>
  scenario.familyId != null
    ? modelDetailPath({ kind: 'family', id: scenario.familyId }, scenario.id)
    : modelDetailPath({ kind: 'client', id: scenario.clientId ?? scope.id }, scenario.id);

export function PeopleAvatars({ names }: { names: string[] }) {
  return (
    <Avatar.Group spacing={6}>
      {names.map(name => (
        <Avatar key={name} size={22} radius="xl" color="gray" aria-hidden>
          {name.charAt(0)}
        </Avatar>
      ))}
    </Avatar.Group>
  );
}

/** The people a group of models covers, and what kind of group it is. */
export function GroupHeading({ people, description }: { people: string[]; description: string }) {
  return (
    <Group gap="xs" px="md" pt="xs" wrap="nowrap">
      {people.length > 0 && <PeopleAvatars names={people} />}
      {people.length > 0 && (
        <Text size="sm" fw={600}>
          {joinNames(people)}
        </Text>
      )}
      <Text size="xs" c="dimmed">
        {description}
      </Text>
    </Group>
  );
}

export function PrimaryMark({ label }: { label: string }) {
  return (
    <Group gap={4} wrap="nowrap">
      <IconStarFilled size={14} color="var(--mantine-color-brand-6)" aria-hidden />
      <Text size="xs" fw={600} c="brand.7">
        {label}
      </Text>
    </Group>
  );
}

export function ModelMeta({ scenario }: { scenario: ScenarioSummary }) {
  const updated = scenario.updatedAt ? formatRelativeDate(scenario.updatedAt) : null;
  const text = scenario.latestCommitIntent
    ? [scenario.latestCommitIntent, updated].filter(Boolean).join(' · ')
    : updated && `Updated ${updated}`;
  return (
    <Group gap="xxs" wrap="nowrap" miw={0}>
      <SyncStatus mode={scenario.syncMode} />
      {text && (
        <Text size="xs" c="dimmed" lineClamp={1} title={text}>
          {text}
        </Text>
      )}
    </Group>
  );
}

export function PendingSyncNotice() {
  return (
    <Group gap="xxs" wrap="nowrap" mt="sm" px="xs" py={6} className={classes.pendingSync}>
      <IconRefresh size={14} color="var(--mantine-color-brand-7)" aria-hidden />
      <Text size="xs" fw={600} c="brand.9" className={classes.grow}>
        The profile has changed since this model was frozen
      </Text>
      <Text size="xs" fw={700} c="brand.7">
        Review changes
      </Text>
    </Group>
  );
}
